/**
 * App-wide unsaved-changes protection.
 *
 * Three escape routes have to be covered, and each needs a different mechanism:
 *
 *   | Route                          | Mechanism                                   |
 *   | ------------------------------ | ------------------------------------------- |
 *   | Refresh / close tab / back-out | `beforeunload` (native browser prompt)      |
 *   | In-app `<Link>` click          | capture-phase click interception + dialog   |
 *   | Browser Back / Forward         | `popstate` re-push + dialog                 |
 *
 * The router here is a plain `<Routes>` tree (not a data router), so
 * `useBlocker` is unavailable; interception is done at the document level.
 *
 * Usage:
 *
 * ```tsx
 * const form = useRef<HTMLFormElement>(null);
 * const { isDirty, reset } = useFormDirty(form);
 * // …
 * <UnsavedChangesGuard isDirty={isDirty} />
 * <form ref={form} onSubmit={() => { save(); reset(); }}>…</form>
 * ```
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from './confirm-dialog';

export const UNSAVED_CHANGES_TITLE = 'Unsaved changes';
export const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes on this form. Leaving now discards them.';
export const UNSAVED_CHANGES_CONFIRM_LABEL = 'Leave anyway';
export const UNSAVED_CHANGES_CANCEL_LABEL = 'Stay on this page';

/** Elements whose value contributes to the dirty snapshot. */
type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function controlValue(control: FormControl): string {
  if (
    control instanceof HTMLInputElement &&
    (control.type === 'checkbox' || control.type === 'radio')
  ) {
    return control.checked ? '1' : '0';
  }
  if (control instanceof HTMLSelectElement && control.multiple) {
    return Array.from(control.selectedOptions)
      .map((option) => option.value)
      .join('\u0000');
  }
  return control.value;
}

/** Stable, order-independent fingerprint of every named control in the form. */
export function snapshotForm(form: HTMLFormElement | null): string {
  if (form === null) {
    return '';
  }
  const controls = Array.from(form.querySelectorAll<FormControl>('input, select, textarea')).filter(
    (control) => !control.disabled && control.dataset.unguarded !== 'true'
  );
  return controls
    .map(
      (control, index) => `${control.name || control.id || `#${index}`}=${controlValue(control)}`
    )
    .join('\n');
}

export interface FormDirtyHandle {
  isDirty: boolean;
  /** Re-baseline after a successful save (or an explicit discard). */
  reset: () => void;
  /** Force the form dirty, e.g. after a change the DOM cannot see. */
  markDirty: () => void;
}

/**
 * Track whether a form differs from the values it had when it mounted.
 *
 * DOM-driven on purpose: it works for controlled and uncontrolled forms alike,
 * needs no per-field wiring, and cannot drift out of sync with the fields a
 * screen actually renders.
 */
export function useFormDirty(formRef: RefObject<HTMLFormElement>): FormDirtyHandle {
  const baselineRef = useRef<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  /**
   * Deferred to a TASK, not a microtask.
   *
   * The listener below sits on the `<form>`, which is *below* React's delegated
   * root listener, so this runs BEFORE React sees the event. A microtask is not
   * late enough to escape that: the browser takes a microtask checkpoint
   * between listener invocations on the same event, so a `queueMicrotask`
   * callback still lands in the middle of React's dispatch. The resulting
   * `setIsDirty` made React restore the controlled input to its committed value
   * and drop that keystroke — the first character a user typed into any guarded
   * form was silently swallowed (it only bit once per form, because `isDirty`
   * only changes false → true on the first edit; later keystrokes set it to the
   * value it already holds, which React bails out of).
   *
   * `setTimeout` runs after the whole dispatch completes. The DOM is already
   * updated by then, so the snapshot is still correct, and at human typing
   * speed the extra turn is imperceptible.
   */
  const timerRef = useRef<number | null>(null);
  const recompute = useCallback(() => {
    // Coalesce bursts (paste, autofill, held key) into one recompute.
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      const baseline = baselineRef.current;
      if (baseline === null) return;
      setIsDirty(snapshotForm(formRef.current) !== baseline);
    }, 0);
  }, [formRef]);

  // A task outlives the component, unlike the microtask this replaced, so a
  // pending recompute must be cancelled or it fires against an unmounted form.
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    []
  );

  const reset = useCallback(() => {
    baselineRef.current = snapshotForm(formRef.current);
    setIsDirty(false);
  }, [formRef]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  // Baseline before paint so the very first `input` event compares correctly.
  useLayoutEffect(() => {
    baselineRef.current = snapshotForm(formRef.current);
  }, [formRef]);

  useEffect(() => {
    const form = formRef.current;
    if (form === null) return undefined;
    form.addEventListener('input', recompute);
    form.addEventListener('change', recompute);
    return () => {
      form.removeEventListener('input', recompute);
      form.removeEventListener('change', recompute);
    };
  }, [formRef, recompute]);

  return { isDirty, reset, markDirty };
}

/* ------------------------------------------------------------------ guard */

export interface UnsavedChangesGuardProps {
  isDirty: boolean;
  /** What the user loses. Defaults to the shared copy. */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Called after the user confirms, before navigation proceeds. */
  onDiscard?: () => void;
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

/** Same-document target we can hand to the SPA router, or `null` to ignore. */
function internalHref(anchor: HTMLAnchorElement): string | null {
  if (anchor.target !== '' && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download')) return null;
  const href = anchor.getAttribute('href');
  if (href === null || href === '' || href.startsWith('#')) return null;
  let url: URL;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search) {
    return null;
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Renders nothing while the form is clean. While dirty it arms the three
 * interception paths and, when one fires, shows a confirm dialog instead of
 * losing the user's work.
 */
export function UnsavedChangesGuard({
  isDirty,
  message = UNSAVED_CHANGES_MESSAGE,
  confirmLabel = UNSAVED_CHANGES_CONFIRM_LABEL,
  cancelLabel = UNSAVED_CHANGES_CANCEL_LABEL,
  onDiscard,
}: UnsavedChangesGuardProps): JSX.Element | null {
  const navigate = useNavigate();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const isDirtyRef = useRef(isDirty);
  const isBypassingRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = isDirty;
    if (!isDirty) setPending(null);
  }, [isDirty]);

  // 1. Refresh / tab close — the native prompt is the only option the platform
  //    gives us; the wording is the browser's, not ours.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (isBypassingRef.current) return;
      event.preventDefault();
      event.returnValue = message;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty, message]);

  // 2. In-app link clicks — intercepted in the capture phase so React Router
  //    never sees the click.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onClick = (event: MouseEvent): void => {
      if (!isDirtyRef.current || isBypassingRef.current || !isPlainLeftClick(event)) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-unsaved-guard-exempt="true"]') !== null) return;
      const anchor = target.closest('a');
      if (anchor === null) return;
      const href = internalHref(anchor);
      if (href === null) return;
      event.preventDefault();
      event.stopPropagation();
      setPending(() => () => navigate(href));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty, navigate]);

  // 3. Browser Back / Forward — `popstate` is not cancellable, so restore the
  //    entry the user just left and ask; confirming replays the move.
  useEffect(() => {
    if (!isDirty || typeof window === 'undefined') return undefined;
    const restoreUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const onPopState = (): void => {
      if (!isDirtyRef.current || isBypassingRef.current) return;
      window.history.pushState(null, '', restoreUrl);
      setPending(() => () => window.history.go(-1));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  if (pending === null) return null;

  const proceed = (): void => {
    isBypassingRef.current = true;
    isDirtyRef.current = false;
    setPending(null);
    onDiscard?.();
    pending();
    // Re-arm on the next tick in case navigation was cancelled downstream.
    window.setTimeout(() => {
      isBypassingRef.current = false;
    }, 0);
  };

  return (
    <div data-unsaved-guard-exempt="true">
      <ConfirmDialog
        title={UNSAVED_CHANGES_TITLE}
        consequence={message}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        tone="danger"
        onConfirm={proceed}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
